import React, { useState, useEffect } from "react";
import { Select as AntdSelect, Button, Tooltip, Empty } from "antd";
import { injectIntl } from "react-intl";
import isequal from "lodash.isequal";
import { CloseCircleOutlined } from "@ant-design/icons";
import { EMPTY_MESSAGE, SELECT_PLACEHOLDER } from "../../modules/language";
import { getMessageString } from "../../tools";

const { Option } = AntdSelect;
const MULTIPLE = "multiple";

const SelectBase = (props) => {
  const {
    intl,
    className: propClass,
    value: propsValue,
    onChange: onChangeProps,
    onFocus: onFocusProps,
    onBlur: onBlurProps,
    placeholder: placeholderProps,
    mode,
    options: optionsProps,
    maxTagCount,
    disabled,
    marker,
  } = props;
  const [selectedItems, setSelectedItems] = useState(propsValue && propsValue !== undefined ? propsValue : []);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [selectProps, setSelectProps] = useState(null);
  const [hasFocus, setHasFocus] = useState(false);
  const required = marker.isRequired;

  useEffect(() => {
    if (hasFocus) {
      const dropdown = document.getElementsByClassName("ldInputDropdown");
      if (dropdown && dropdown !== undefined)
        for (let item of dropdown) {
          for (let child of item.children[0].children) {
            if (child.getAttribute("role") === "listbox") child.setAttribute("aria-label", child.id + "_ariaLabel");
          }
        }
    } else {
      const select = document.getElementsByClassName("ldSelect");
      if (select && select !== undefined)
        for (let item of select) {
          for (let children of item.children[0].children) {
            if (children.className === "ant-select-selection-search") {
              const child = children.children[0];
              if (child && child !== undefined && child.getAttribute("role") === "combobox")
                child.removeAttribute("role");
            }
          }
        }
    }
  }, [hasFocus]);

  useEffect(() => {
    const { className, placeholder, onChange, onFocus, onBlur, allowClear, value, bordered, filterOption, notFoundContent, options, ...selectPropsCopy } = Object.assign({}, props); //prettier-ignore
    const fullClassName = propClass !== undefined ? "ldSelect " + propClass : "ldSelect";
    selectPropsCopy.className = fullClassName;
    if (!isequal(selectPropsCopy, selectProps)) setSelectProps(selectPropsCopy);
    // eslint-disable-next-line
  }, [selectProps, props]);

  useEffect(() => {
    if (optionsProps && optionsProps !== undefined && !isequal(props.value, selectedKeys)) {
      const newValue = !props.value || props.value === undefined ? [] : props.value;
      if (newValue.length === 0) setHasFocus(false);
      setSelectedKeys(newValue);
      setSelectedItems(newValue.map((key) => optionsProps.find((item) => item.id === key).value));
    } else if (
      (!optionsProps || optionsProps !== undefined) &&
      (!props.value || props.value === undefined) &&
      !isequal(props.value, selectedKeys)
    ) {
      setHasFocus(false);
      setSelectedKeys([]);
      setSelectedItems([]);
    }
    // eslint-disable-next-line
  }, [props, optionsProps]);

  const removeItem = (item) => {
    let selectedItemsTmp = Object.assign({}, selectedItems);
    const index = selectedItemsTmp.indexOf(item);
    if (index > -1) selectedItemsTmp.splice(index, 1);
    onChangeItems(selectedItemsTmp);
  };

  const MaxTagPlaceholder = () => (
    <div>
      <Tooltip
        title={
          <div className="maxTagPlaceholder">
            {selectedItems.slice(maxTagCount).map((item, index) => (
              <div className="maxTagPlaceholderContent" key={index}>
                <span className="maxTagPlaceholderItem">{item}</span>
                <Button onClick={() => removeItem(item)} icon={<CloseCircleOutlined />} />
              </div>
            ))}
          </div>
        }
      >
        + {selectedItems.length - maxTagCount} ...
      </Tooltip>
    </div>
  );

  const onChangeItems = (choosenItems) => {
    // prettier-ignore
    const choosenKeys = optionsProps && optionsProps !== undefined && mode === MULTIPLE
      ? choosenItems.length > 0
        ? choosenItems.length > selectedKeys.length
            ? [...selectedKeys, ...choosenItems.filter((item) => !selectedItems.includes(item))]
            : selectedKeys.filter((key) => choosenItems.includes(optionsProps.find((data) => data.id === key).value))
        : []
      : choosenItems !== undefined ? [choosenItems] : [];
    setSelectedKeys(choosenKeys);
    if (onChangeProps && onChangeProps !== undefined) onChangeProps({ target: { value: choosenKeys } });
    if (optionsProps && optionsProps !== undefined)
      setSelectedItems(choosenKeys.map((key) => optionsProps.find((item) => item.id === key).value));
  };

  const onBlurChange = (e) => {
    let tempE = Object.assign({}, e);
    if (e.target.value === undefined || e.target.value.length === 0) tempE.target = { value: null };
    if (hasFocus) setHasFocus(false);
    if (onBlurProps && onBlurProps !== undefined) onBlurProps(tempE);
  };

  const onFocusChange = (e) => {
    if (!hasFocus) setHasFocus(true);
    if (onFocusProps && onFocusProps !== undefined) onFocusProps(e);
  };

  let labelClassName = "ldSelectLabel";
  if (hasFocus || (selectedKeys && selectedKeys !== undefined && selectedKeys.length > 0)) labelClassName += " selectActive"; //prettier-ignore
  if (disabled !== undefined && disabled === true) labelClassName += " selectDisabled";

  const filteredData =
    optionsProps && optionsProps !== undefined && optionsProps.length > 0
      ? optionsProps.filter((item) => !selectedItems.includes(item.value))
      : [];

  return (
    <div className="selectContent">
      <div className={labelClassName}>
        {required && <span className="requiredInput">*</span>}
        {placeholderProps === undefined ? getMessageString(intl, SELECT_PLACEHOLDER) : placeholderProps}
      </div>
      <AntdSelect
        value={selectedItems}
        onChange={onChangeItems}
        allowClear
        onFocus={onFocusChange}
        onBlur={onBlurChange}
        {...(mode && mode !== undefined && mode === MULTIPLE && { mode: MULTIPLE })}
        {...((!mode || mode === undefined) && { showSearch: true })}
        bordered={false}
        filterOption={(inputValue, option) =>
          option.props.children.toString().toLowerCase().includes(inputValue.toLowerCase())
        }
        notFoundContent={<Empty description={getMessageString(intl, EMPTY_MESSAGE)} />}
        dropdownClassName="ldInputDropdown"
        // prettier-ignore
        {...(mode && mode !== undefined && mode === MULTIPLE && maxTagCount && maxTagCount !== undefined && {
          maxTagCount: maxTagCount,
          maxTagPlaceholder: MaxTagPlaceholder,
        })}
        {...selectProps}
      >
        {filteredData.map((item) => (
          <Option key={item.id} disabled={item.disabled}>
            {item.value}
          </Option>
        ))}
      </AntdSelect>
    </div>
  );
};

export const Select = injectIntl(SelectBase);
